/**
 * @fileoverview Notification preferences API calls.
 * @description GET and PUT /notifications/preferences (Confluence 3.5.9.7 and 3.5.9.8): the
 * signed-in user's per-channel notification preferences, for notification types and categories.
 * Self-service, so no permission is needed. Every row carries an opaque `rowVersion` the PUT must
 * echo back; a stale or missing one is rejected with 409/6007, after which the caller refetches.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { httpClient } from '@/shared/api/http-client'

export type NotificationPreferenceScope = 'TYPE' | 'CATEGORY'

/** One channel of one notification type or category (a single preference row). */
export interface NotificationPreference {
  preferenceId?: string | null
  preferenceScope: NotificationPreferenceScope
  notificationType?: string | undefined
  category?: string | undefined
  /** EMAIL, PUSH or IN_APP today — the backend may add more, so it stays a plain string. */
  channel: string
  /** The user's own choice; null when they never set one. */
  userPreference: boolean | null
  /** What actually applies after defaults and overrides; null when undetermined. */
  effectiveEnabled: boolean | null
  /** Only on TYPE rows: the notification can't be turned off. */
  isMandatory?: boolean
  isConfigurable: boolean
  source: string
  overrideSource?: string | null
  effectiveFrom?: string | null
  /** Opaque version token for the PUT; null when the user has no stored row yet. */
  rowVersion: string | null
}

export interface NotificationPreferencesResponse {
  preferences: NotificationPreference[]
}

/** One target of the PUT body. `isEnabled: null` resets the preference to its default. */
export interface NotificationPreferenceUpdate {
  preferenceScope: NotificationPreferenceScope
  notificationType?: string
  category?: string
  channel: string
  isEnabled: boolean | null
  rowVersion?: string
}

/**
 * @description Fetches the signed-in user's notification preferences.
 * @returns {Promise<NotificationPreference[]>} One row per type/category and channel.
 */
export async function getNotificationPreferences(): Promise<NotificationPreference[]> {
  const { data } = await httpClient.get<NotificationPreferencesResponse>(
    '/notifications/preferences',
  )
  return data.preferences
}

/**
 * @description Saves the given preference changes in one atomic request (all or nothing).
 * @param {NotificationPreferenceUpdate[]} preferences - 1 to 100 unique targets to change.
 */
export async function updateNotificationPreferences(
  preferences: NotificationPreferenceUpdate[],
): Promise<void> {
  await httpClient.put('/notifications/preferences', { preferences })
}
